import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { apiService } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  MapPin, 
  BookOpen,
  Users,
  Check,
  X,
  CheckCircle2,
  Loader2,
  FileText,
  User,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { gerarPDFEncontro } from '@/lib/pdfGenerator';

export const EncontroDetalhesPage = () => {
  const { encontroId } = useParams();
  const navigate = useNavigate();
  const { igrejaAtual } = useApp();
  
  const [encontro, setEncontro] = useState(null);
  const [celula, setCelula] = useState(null);
  const [membros, setMembros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  
  // Track presence changes
  const [presencas, setPresencas] = useState({});

  useEffect(() => {
    if (encontroId && igrejaAtual?.id) {
      loadEncontro();
    }
  }, [encontroId, igrejaAtual?.id]);

  const loadEncontro = async () => {
    try {
      setLoading(true);
      
      // Load encontro details
      const encontroData = await apiService.getEncontro(encontroId);
      setEncontro(encontroData);
      
      // Load celula
      if (encontroData.celula_id && igrejaAtual?.id) {
        const celulaData = await apiService.getCelula(igrejaAtual.id, encontroData.celula_id);
        setCelula(celulaData);
        
        // Load celula members
        const membrosData = await apiService.getCelulaMembros(igrejaAtual.id, encontroData.celula_id);
        setMembros(membrosData);
        
        // Initialize presence state
        const initialPresencas = {};
        membrosData.forEach(m => {
          if (encontroData.presentes?.includes(m.igreja_membro_id)) {
            initialPresencas[m.igreja_membro_id] = true;
          } else if (encontroData.ausentes?.includes(m.igreja_membro_id)) {
            initialPresencas[m.igreja_membro_id] = false;
          } else {
            initialPresencas[m.igreja_membro_id] = null; // Not marked yet
          }
        });
        setPresencas(initialPresencas);
      }
    } catch (error) {
      console.error('Error loading encontro:', error);
      toast.error('Erro ao carregar encontro');
    } finally {
      setLoading(false);
    }
  };

  const handlePresencaChange = async (membroId, presente) => {
    if (!igrejaAtual?.id || !celula?.id || encontro?.status === 'realizado') return;
    
    // Optimistic update
    setPresencas(prev => ({ ...prev, [membroId]: presente }));
    
    try {
      setSaving(true);
      await apiService.registrarPresenca(igrejaAtual.id, celula.id, encontroId, membroId, presente);
      
      // Update encontro state
      setEncontro(prev => {
        const newPresentes = presente 
          ? [...(prev.presentes || []).filter(id => id !== membroId), membroId]
          : (prev.presentes || []).filter(id => id !== membroId);
        const newAusentes = !presente
          ? [...(prev.ausentes || []).filter(id => id !== membroId), membroId]
          : (prev.ausentes || []).filter(id => id !== membroId);
        
        return {
          ...prev,
          presentes: newPresentes,
          ausentes: newAusentes
        };
      });
    } catch (error) {
      // Revert on error
      setPresencas(prev => ({ ...prev, [membroId]: !presente }));
      toast.error('Erro ao registrar presença');
    } finally {
      setSaving(false);
    }
  };

  const handleFinalizar = async () => {
    if (!igrejaAtual?.id || !celula?.id) return;
    
    try {
      setFinalizing(true);
      await apiService.finalizarEncontro(igrejaAtual.id, celula.id, encontroId);
      setEncontro(prev => ({ ...prev, status: 'realizado' }));
      toast.success('Encontro finalizado com sucesso!');
    } catch (error) {
      console.error('Error finalizing encontro:', error);
      toast.error('Erro ao finalizar encontro');
    } finally {
      setFinalizing(false);
    }
  };

  const handleGerarPDF = () => {
    if (encontro && celula) {
      gerarPDFEncontro({ ...encontro, membros }, celula);
      toast.success('PDF gerado!');
    }
  };

  // Mark all as present
  const handleMarcarTodosPresentes = async () => {
    for (const membro of membros) {
      if (presencas[membro.igreja_membro_id] !== true) {
        await handlePresencaChange(membro.igreja_membro_id, true);
      }
    }
  };

  if (loading) {
    return (
      <div className="page-transition">
        <div className="px-6 lg:px-8 py-8">
          <div className="max-w-4xl mx-auto">
            <Skeleton className="h-8 w-48 mb-8" />
            <Skeleton className="h-64 w-full rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!encontro) {
    return (
      <div className="page-transition">
        <div className="px-6 lg:px-8 py-12">
          <div className="max-w-md mx-auto text-center">
            <AlertCircle className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-2xl font-semibold mb-2">Encontro não encontrado</h2>
            <Button asChild>
              <Link to="/encontros">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const totalPresentes = Object.values(presencas).filter(v => v === true).length;
  const totalAusentes = Object.values(presencas).filter(v => v === false).length;
  const totalPendentes = Object.values(presencas).filter(v => v === null).length;
  const isFinalized = encontro.status === 'realizado';

  return (
    <div className="page-transition" data-testid="encontro-detalhes-page">
      {/* Header */}
      <div className="px-6 lg:px-8 py-6 bg-gradient-to-r from-blue-500/10 via-primary/10 to-green-500/10 border-b border-border">
        <div className="max-w-4xl mx-auto">
          <Button variant="ghost" size="sm" asChild className="mb-4">
            <Link to={celula ? `/celulas/${celula.id}` : '/encontros'}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {celula ? `Voltar para ${celula.nome}` : 'Voltar'}
            </Link>
          </Button>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="font-heading text-2xl lg:text-3xl font-bold text-foreground">
                  Encontro - {format(new Date(encontro.data), "d 'de' MMMM", { locale: ptBR })}
                </h1>
                <Badge 
                  variant={isFinalized ? 'secondary' : encontro.status === 'cancelado' ? 'destructive' : 'default'}
                  className="text-sm"
                >
                  {isFinalized ? 'Realizado' : encontro.status === 'cancelado' ? 'Cancelado' : 'Agendado'}
                </Badge>
              </div>
              {celula && (
                <p className="text-muted-foreground">{celula.nome}</p>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleGerarPDF}>
                <FileText className="w-4 h-4 mr-2" />
                PDF
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 lg:px-8 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Info Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="card-elevated">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Calendar className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Data</p>
                  <p className="font-medium">
                    {format(new Date(encontro.data), "EEEE, d/MM", { locale: ptBR })}
                  </p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-elevated">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Clock className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Horário</p>
                  <p className="font-medium">{encontro.horario}</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-elevated">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <MapPin className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Local</p>
                  <p className="font-medium truncate">{encontro.local || 'Não definido'}</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="card-elevated">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <BookOpen className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Estudo</p>
                  <p className="font-medium truncate">
                    {encontro.livro ? `${encontro.livro} ${encontro.capitulo || ''}` : encontro.assuntos || 'Não definido'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Presence Stats */}
          <Card className="card-elevated">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-primary" />
                    Lista de Presença
                  </CardTitle>
                  <CardDescription>
                    {membros.length} membros na célula
                  </CardDescription>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{totalPresentes}</p>
                    <p className="text-xs text-muted-foreground">Presentes</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-500">{totalAusentes}</p>
                    <p className="text-xs text-muted-foreground">Ausentes</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-muted-foreground">{totalPendentes}</p>
                    <p className="text-xs text-muted-foreground">Pendentes</p>
                  </div>
                </div>
              </div>
            </CardHeader>
            
            <Separator />
            
            <CardContent className="pt-4">
              {!isFinalized && (
                <div className="flex gap-2 mb-4">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleMarcarTodosPresentes}
                    disabled={saving}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Marcar todos presentes
                  </Button>
                </div>
              )}
              
              <div className="space-y-2">
                {membros.map((membro) => {
                  const isPresente = presencas[membro.igreja_membro_id];
                  
                  return (
                    <div 
                      key={membro.id}
                      className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                        isPresente === true 
                          ? 'bg-green-50 dark:bg-green-900/20' 
                          : isPresente === false 
                            ? 'bg-red-50 dark:bg-red-900/20' 
                            : 'bg-muted/30'
                      }`}
                      data-testid={`membro-presenca-${membro.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{membro.user_nome}</p>
                          <p className="text-xs text-muted-foreground">{membro.role}</p>
                        </div>
                      </div>
                      
                      {isFinalized ? (
                        <Badge variant={isPresente ? 'default' : 'destructive'}>
                          {isPresente ? (
                            <><Check className="w-3 h-3 mr-1" /> Presente</>
                          ) : (
                            <><X className="w-3 h-3 mr-1" /> Ausente</>
                          )}
                        </Badge>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Button
                            variant={isPresente === true ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => handlePresencaChange(membro.igreja_membro_id, true)}
                            disabled={saving}
                            className={isPresente === true ? 'bg-green-600 hover:bg-green-700' : ''}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            variant={isPresente === false ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => handlePresencaChange(membro.igreja_membro_id, false)}
                            disabled={saving}
                            className={isPresente === false ? 'bg-red-600 hover:bg-red-700' : ''}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {membros.length === 0 && (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">Nenhum membro na célula</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Finalize Button */}
          {!isFinalized && encontro.status !== 'cancelado' && (
            <Card className="card-elevated border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-900/10">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Finalizar Encontro</h3>
                    <p className="text-sm text-muted-foreground">
                      Ao finalizar, a lista de presença será registrada e o encontro será marcado como realizado.
                    </p>
                    {totalPendentes > 0 && (
                      <p className="text-sm text-orange-600 mt-1">
                        <AlertCircle className="w-4 h-4 inline mr-1" />
                        {totalPendentes} membro(s) ainda não marcado(s)
                      </p>
                    )}
                  </div>
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        size="lg" 
                        className="bg-green-600 hover:bg-green-700"
                        disabled={finalizing}
                        data-testid="finalizar-encontro-btn"
                      >
                        {finalizing ? (
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        ) : (
                          <CheckCircle2 className="w-5 h-5 mr-2" />
                        )}
                        Finalizar Encontro
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Finalizar Encontro?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Esta ação irá marcar o encontro como realizado e registrar a presença final.
                          <br /><br />
                          <strong>Resumo:</strong>
                          <br />• {totalPresentes} presentes
                          <br />• {totalAusentes} ausentes
                          {totalPendentes > 0 && <><br />• {totalPendentes} não marcados (serão considerados ausentes)</>}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={handleFinalizar}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          Confirmar Finalização
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Cronograma (if exists) */}
          {encontro.cronograma && encontro.cronograma.length > 0 && (
            <Card className="card-elevated">
              <CardHeader>
                <CardTitle>Cronograma</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {encontro.cronograma.map((item, index) => (
                    <div key={item.id || index} className="flex items-start gap-4 p-3 bg-muted/30 rounded-lg">
                      <div className="text-sm font-medium text-primary whitespace-nowrap">
                        {item.hora_inicio} - {item.hora_fim}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{item.atividade}</p>
                        {item.responsaveis && item.responsaveis.length > 0 && (
                          <p className="text-sm text-muted-foreground">
                            {item.responsaveis.join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};
